import { ComponentType, LazyExoticComponent } from 'react';

// ============================================================================
// Breakpoint Types
// ============================================================================

export type BreakpointKey = 'base' | 'sm' | 'md' | 'lg' | 'xl' | 'tv';

export interface WidgetSize {
  /** Number of grid columns to span (out of 12) */
  cols: number;
  /** Number of grid rows to span */
  rows: number;
}

export type WidgetSizeConfig = Partial<Record<BreakpointKey, WidgetSize>> & {
  /** Required: Mobile/base size */
  base: WidgetSize;
};

// ============================================================================
// Widget Data Types
// ============================================================================

export type DataSourceType = 'global' | 'self' | 'hybrid';

export interface WidgetDataConfig {
  /** How the widget gets its data */
  sourceType: DataSourceType;
  /** Keys from global dashboard data to subscribe to (for 'global' or 'hybrid') */
  globalDataKeys?: string[];
  /** Default filter values */
  defaultFilters?: Record<string, unknown>;
  /** Auto-refresh interval in milliseconds */
  refreshInterval?: number;
}

export type FilterLocation = 'header' | 'inline' | 'both';

// ============================================================================
// Widget Category
// ============================================================================

export type WidgetCategory =
  | 'stats'
  | 'charts'
  | 'lists'
  | 'tasks'
  | 'people'
  | 'projects'
  | 'meetings';

export interface WidgetCategoryInfo {
  key: WidgetCategory;
  label: string;
  description: string;
}

export const WIDGET_CATEGORIES: WidgetCategoryInfo[] = [
  { key: 'stats', label: 'Statistics', description: 'Metrics and KPIs' },
  { key: 'charts', label: 'Charts', description: 'Visual data representations' },
  { key: 'lists', label: 'Lists', description: 'Data lists and feeds' },
  { key: 'tasks', label: 'Tasks', description: 'Task management widgets' },
  { key: 'people', label: 'People', description: 'Team and member widgets' },
  { key: 'projects', label: 'Projects', description: 'Project tracking widgets' },
  { key: 'meetings', label: 'Meetings', description: 'Calendar and meeting widgets' },
];

// ============================================================================
// Widget Definition (Registry)
// ============================================================================

export interface WidgetDefinition<TProps extends WidgetProps = WidgetProps> {
  /** Unique widget identifier */
  id: string;
  /** Display name */
  name: string;
  /** Short description */
  description: string;
  /** Icon component */
  icon: ComponentType<{ size?: number }>;
  /** Widget category for grouping */
  category: WidgetCategory;
  /** Lazy-loaded widget component */
  component: LazyExoticComponent<ComponentType<TProps>>;
  /** Size configuration per breakpoint (default size) */
  sizes: WidgetSizeConfig;
  /** Size variant configuration for resizable widgets */
  sizeVariants?: WidgetSizeVariantConfig;
  /** Data source configuration */
  data: WidgetDataConfig;
  /** Required permissions to use this widget */
  permissions?: string[];
  /** Default props passed to widget */
  defaultProps?: Record<string, unknown>;
  /** Whether widget can be collapsed (default: true) */
  collapsible?: boolean;
  /** Where filters should be displayed */
  filterLocation?: FilterLocation;
  /** Whether widget supports filters */
  hasFilters?: boolean;
  /** Whether widget supports settings customization */
  hasSettings?: boolean;
  /** Whether widget can be expanded to full screen */
  expandable?: boolean;
  /** Whether to show the header bar (default: true) */
  hasHeader?: boolean;
  /** Widget display style - 'stat' widgets are headerless with hover controls */
  displayStyle?: 'card' | 'stat' | 'minimal';
}

// ============================================================================
// Widget Instance (User's Dashboard)
// ============================================================================

export interface WidgetInstance {
  /** Unique instance ID */
  instanceId: string;
  /** Reference to widget definition ID */
  widgetId: string;
  /** Position order in the dashboard */
  order: number;
  /** Whether widget is collapsed */
  isCollapsed: boolean;
  /** User-configured filters */
  filters?: Record<string, unknown>;
  /** Custom props override */
  customProps?: Record<string, unknown>;
  /** Widget settings (size variant, refresh interval, etc.) */
  settings?: WidgetSettings;
}

// ============================================================================
// Widget Props (Passed to Widget Component)
// ============================================================================

export interface WidgetProps {
  /** Widget definition ID */
  widgetId: string;
  /** Unique instance ID */
  instanceId: string;
  /** Whether widget is currently collapsed */
  isCollapsed: boolean;
  /** Global dashboard data (if using global source) */
  globalData?: DashboardGlobalData;
  /** Current filter values */
  filters?: Record<string, unknown>;
  /** Callback to update filters */
  onFilterChange?: (filters: Record<string, unknown>) => void;
  /** Widget settings including size variant */
  settings?: WidgetSettings;
}

// ============================================================================
// Dashboard Global Data
// ============================================================================

export interface DashboardGlobalData {
  // Raw data
  projects: ProjectData[];
  tasks: TaskData[];
  users: UserData[];
  invoices: InvoiceData[];
  meetings: MeetingData[];
  leaveRequests: LeaveRequestData[];

  // Computed statistics
  invoiceStats: InvoiceStats;
  taskStats: TaskStats;
  projectStats: ProjectStats;
}

// Data interfaces for widgets
export interface ProjectData {
  id: string;
  name: string;
  description: string;
  status: 'planning' | 'in-progress' | 'on-hold' | 'completed' | 'cancelled';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  progress: number;
  ownerId: string;
  memberIds: string[];
  startDate: string;
  dueDate: string;
  endDate?: string;
  budget?: number;
  tags: string[];
  category: string;
}

export interface TaskData {
  id: string;
  title: string;
  description: string;
  projectId: string;
  assigneeId?: string;
  reporterId: string;
  status: 'todo' | 'in-progress' | 'review' | 'done' | 'blocked';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  tags: string[];
  dueDate?: string;
  estimatedHours?: number;
  actualHours?: number;
  location?: string;
}

export interface UserData {
  id: string;
  name: string;
  email: string;
  avatar?: string;
  role: string;
  department: string;
  title: string;
  status: 'active' | 'away' | 'busy' | 'offline';
}

export interface InvoiceData {
  id: string;
  number: string;
  clientName: string;
  amount: number;
  status: 'draft' | 'pending' | 'paid' | 'overdue' | 'cancelled';
  dueDate: string;
  createdAt: string;
}

export interface MeetingData {
  id: string;
  title: string;
  description?: string;
  startTime: string;
  endTime: string;
  location?: string;
  attendeeIds: string[];
  organizerId: string;
  type: 'meeting' | 'call' | 'presentation' | 'workshop';
}

export interface LeaveRequestData {
  id: string;
  userId: string;
  type: 'vacation' | 'sick' | 'personal' | 'other';
  startDate: string;
  endDate: string;
  status: 'pending' | 'approved' | 'rejected';
  reason?: string;
}

// Statistics interfaces
export interface InvoiceStats {
  total: number;
  totalAmount: number;
  pending: number;
  pendingAmount: number;
  paid: number;
  paidAmount: number;
  overdue: number;
  overdueAmount: number;
}

export interface TaskStats {
  total: number;
  todo: number;
  inProgress: number;
  review: number;
  done: number;
  blocked: number;
  overdue: number;
}

export interface ProjectStats {
  total: number;
  planning: number;
  inProgress: number;
  onHold: number;
  completed: number;
  cancelled: number;
  averageProgress: number;
}

// ============================================================================
// Widget Size Variants
// ============================================================================

/** Size variant options for stat widgets (width-based) */
export type StatWidgetSizeVariant = 'compact' | 'standard' | 'wide' | 'full';

/** Size variant options for list widgets (height-based) */
export type ListWidgetSizeVariant = 'compact' | 'standard' | 'tall' | 'wide';

/** Size variant options for chart widgets (both dimensions) */
export type ChartWidgetSizeVariant = 'small' | 'medium' | 'large' | 'full';

/** Size variant options for individual stat widgets (headerless) */
export type IndividualStatSizeVariant = 'tiny' | 'small' | 'medium' | 'large';

/** Generic size variant type */
export type WidgetSizeVariant = StatWidgetSizeVariant | ListWidgetSizeVariant | ChartWidgetSizeVariant | IndividualStatSizeVariant;

/** Size variant configuration for a widget */
export interface WidgetSizeVariantConfig {
  /** Available size variants for this widget */
  variants: WidgetSizeVariant[];
  /** Default variant */
  defaultVariant: WidgetSizeVariant;
  /** Size configuration per variant per breakpoint (only needs to include the variants listed in 'variants') */
  variantSizes: Partial<Record<WidgetSizeVariant, WidgetSizeConfig>>;
}

// ============================================================================
// Widget Settings
// ============================================================================

export interface WidgetSettings {
  /** Size variant selection (for desktop - lg, xl, tv breakpoints) */
  sizeVariant?: WidgetSizeVariant;
  /** Size variant for tablet breakpoints (md) - falls back to sizeVariant if not set */
  tabletSizeVariant?: WidgetSizeVariant;
  /** Size variant for phone breakpoints (base, sm) - falls back to tabletSizeVariant or sizeVariant if not set */
  phoneSizeVariant?: WidgetSizeVariant;
  /** Auto-refresh enabled */
  autoRefresh?: boolean;
  /** Refresh interval in seconds */
  refreshInterval?: number;
  /** Show header */
  showHeader?: boolean;
  /** Custom title override */
  customTitle?: string;
  /** Theme variant */
  themeVariant?: 'default' | 'gradient' | 'minimal' | 'custom';
  /** Custom background color (when themeVariant is 'custom') */
  backgroundColor?: string;
  /** Custom gradient background (when themeVariant is 'gradient' or 'custom') */
  backgroundGradient?: string;
  /** Text color - 'auto' to calculate based on background, or a specific color */
  textColor?: 'auto' | string;
  /** Computed text color (calculated from backgroundColor when textColor is 'auto') */
  computedTextColor?: string;
}

// ============================================================================
// Widget Action Types
// ============================================================================

export type WidgetAction = 'refresh' | 'filter' | 'expand' | 'settings' | 'remove';

export interface WidgetActionHandler {
  action: WidgetAction;
  handler: () => void;
  label: string;
  icon?: React.ComponentType<{ size?: number }>;
  disabled?: boolean;
}

// ============================================================================
// Grid Position (for layout engine)
// ============================================================================

export interface GridPosition {
  /** Starting column (0-based) */
  col: number;
  /** Starting row (0-based) */
  row: number;
  /** Number of columns to span */
  width: number;
  /** Number of rows to span */
  height: number;
}

export interface PlacedWidget {
  instanceId: string;
  widgetId: string;
  position: GridPosition;
}

// ============================================================================
// User Statistics
// ============================================================================

export interface UserStats {
  total: number;
  active: number;
  away: number;
  busy: number;
  offline: number;
  onLeave: number;
}

// ============================================================================
// Container Types (Widget Grouping)
// ============================================================================

/** Container layout templates */
export type ContainerLayout =
  | '2x2-grid'      // 4 equal cells (2 cols × 2 rows)
  | '1x2-row'       // 2 equal horizontal cells
  | '1x3-row'       // 3 equal horizontal cells
  | '1x4-row'       // 4 equal horizontal cells
  | '2x1-col'       // 2 vertical cells
  | '3x1-col'       // 3 vertical cells
  | '3x2-grid'      // 6 cells (3 cols × 2 rows)
  | 'flexible';     // Custom cols/rows per child

/** Slot definition within a container */
export interface ContainerSlot {
  /** Unique slot identifier */
  id: string;
  /** Internal grid column (0-based) */
  col: number;
  /** Internal grid row (0-based) */
  row: number;
  /** Number of columns to span (default: 1) */
  colSpan?: number;
  /** Number of rows to span (default: 1) */
  rowSpan?: number;
}

/** Container definition (registered like widgets) */
export interface ContainerDefinition {
  /** Unique container identifier */
  id: string;
  /** Display name */
  name: string;
  /** Short description */
  description: string;
  /** Icon component */
  icon: ComponentType<{ size?: number }>;
  /** Layout template type */
  layout: ContainerLayout;
  /** Container sizes at each breakpoint */
  sizes: WidgetSizeConfig;
  /** Size variant configuration for resizable containers */
  sizeVariants?: WidgetSizeVariantConfig;
  /** Internal grid configuration */
  internalGrid: {
    /** Number of internal columns */
    cols: number;
    /** Number of internal rows */
    rows: number;
  };
  /** Slot definitions for pre-defined layouts */
  slots: ContainerSlot[];
}

/** Child widget within a container */
export interface ContainerChild {
  /** Unique instance ID for this child */
  instanceId: string;
  /** Reference to widget definition ID */
  widgetId: string;
  /** Slot ID this child occupies (for pre-defined layouts) */
  slotId: string;
  /** Widget settings for this child */
  settings?: WidgetSettings;
}

/** Container instance (stored in dashboard) */
export interface ContainerInstance {
  /** Unique instance ID */
  instanceId: string;
  /** Reference to container definition ID */
  containerId: string;
  /** Position order in main dashboard grid */
  order: number;
  /** Child widgets in this container */
  children: ContainerChild[];
  /** Container settings (size variant, etc.) */
  settings?: WidgetSettings;
}

/** Placed container with calculated position */
export interface PlacedContainer {
  instanceId: string;
  containerId: string;
  position: GridPosition;
}

/** Union type for placed items in the grid */
export type PlacedItem =
  | { type: 'widget'; item: PlacedWidget }
  | { type: 'container'; item: PlacedContainer };
